import React from 'react';
import { motion, type Variants } from 'motion/react';
import { Card } from './Card';
import { Button } from './Button';

export interface EmptyStateAction {
  label: string;
  onClick?: () => void;
  href?: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  icon?: React.ReactNode;
}

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description: string;
  actions?: EmptyStateAction[];
  className?: string;
}

/**
 * EmptyState - Consistent empty state component with helpful guidance
 *
 * Usage:
 * ```tsx
 * <EmptyState
 *   icon={<BookOpen className="w-8 h-8" />}
 *   title="No trades yet"
 *   description="Start tracking your trading performance by logging your first trade."
 *   actions={[
 *     { label: 'Log Your First Trade', onClick: handleNewTrade, variant: 'primary' },
 *     { label: 'Learn More', href: '/help', variant: 'ghost' }
 *   ]}
 * />
 * ```
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  actions = [],
  className = '',
}) => {
  // Animation variants for staggered reveal
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  // Floating animation for the icon
  const floatVariants: Variants = {
    animate: {
      y: [0, -8, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <Card
      hoverEffect={false}
      className={`p-8 sm:p-12 text-center overflow-hidden ${className}`}
      role="status"
      aria-label={title}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative"
      >
        {/* Subtle background decoration */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-st-primary/5 rounded-full blur-3xl" />
        </div>

        {/* Icon */}
        {icon && (
          <motion.div
            variants={itemVariants}
            className="relative mx-auto mb-6"
          >
            <motion.div
              variants={floatVariants}
              animate="animate"
              className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-st-slate to-st-card flex items-center justify-center text-st-primary border border-slate-700/50 shadow-lg shadow-st-primary/5"
              aria-hidden="true"
            >
              {icon}
            </motion.div>
            {/* Decorative rings */}
            <div className="absolute inset-0 -z-10">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 rounded-full border border-st-primary/10" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36 rounded-full border border-st-primary/5" />
            </div>
          </motion.div>
        )}

        {/* Title */}
        <motion.h3
          variants={itemVariants}
          className="text-xl font-bold text-white mb-3"
        >
          {title}
        </motion.h3>

        {/* Description */}
        <motion.p
          variants={itemVariants}
          className="text-slate-400 mb-8 max-w-md mx-auto leading-relaxed"
        >
          {description}
        </motion.p>

        {/* Actions */}
        {actions.length > 0 && (
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row items-center justify-center gap-3"
            role="group"
            aria-label="Available actions"
          >
            {actions.map((action, index) => {
              if (action.href) {
                return (
                  <motion.a
                    key={index}
                    href={action.href}
                    className={getButtonClasses(action.variant || 'primary')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {action.icon && <span aria-hidden="true">{action.icon}</span>}
                    {action.label}
                  </motion.a>
                );
              }
              return (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    type="button"
                    variant={action.variant || 'primary'}
                    onClick={action.onClick}
                  >
                    {action.icon && <span aria-hidden="true">{action.icon}</span>}
                    {action.label}
                  </Button>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </motion.div>
    </Card>
  );
};

/**
 * Helper to get button-like classes for anchor elements
 * Includes proper focus states for accessibility
 */
function getButtonClasses(variant: string): string {
  const base = [
    'inline-flex items-center justify-center rounded-md font-bold',
    'transition-all duration-300 tracking-wide px-6 py-3 text-base',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-st-dark',
  ].join(' ');

  const variants: Record<string, string> = {
    primary: 'bg-st-primary text-white hover:bg-[#12C4BE] shadow-lg shadow-st-primary/20 focus-visible:ring-st-primary',
    secondary: 'bg-slate-700 text-white hover:bg-slate-600 focus-visible:ring-slate-500',
    outline: 'border-2 border-st-primary text-st-primary hover:bg-st-primary hover:text-white focus-visible:ring-st-primary',
    ghost: 'text-st-muted hover:text-st-primary focus-visible:ring-st-primary',
  };

  return `${base} ${variants[variant] || variants.primary}`;
}

export default EmptyState;
